'use client';

import React, { useEffect, useRef, useState } from 'react';
import Chips from '@/components/chips/Chips';
import clsx from 'clsx';
import { ChipsWrapperProps } from '@/components/chips/types';

const ChipsWrapper: React.FC<ChipsWrapperProps> = ({
  options,
  selectedOptions,
  onSelectionChange,
  variant,
  selectionMode = 'multiple',
  resetPosition,
}) => {
  const [selectedChips, setSelectedChips] = useState<string[]>(selectedOptions);
  const containerRef = useRef<HTMLDivElement>(null);
  const chipsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSelectedChips(selectedOptions);
  }, [selectedOptions]);

  useEffect(() => {
    if (resetPosition && containerRef.current) {
      containerRef.current.scrollLeft = 0;
    }
  }, [resetPosition]);

  const handleChipClick = (chip: string) => {
    let updatedSelection: string[];

    if (selectionMode === 'multiple') {
      // Prevent deselection
      if (!selectedChips.includes(chip)) {
        updatedSelection = [...selectedChips, chip];
      } else {
        updatedSelection = selectedChips; // No change if already selected
      }
    } else {
      updatedSelection = [chip]; // Single selection mode
    }

    setSelectedChips(updatedSelection);
    onSelectionChange(updatedSelection);
  };

  const scrollToChip = (chipElement: HTMLElement) => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      const chipLeft = chipElement.offsetLeft;
      const chipWidth = chipElement.offsetWidth;

      const scrollPosition = chipLeft - (containerWidth - chipWidth) / 2;

      containerRef.current.scrollTo({
        left: scrollPosition,
        behavior: 'smooth',
      });
    }
  };

  const handleChipClickMobile = (chip: string, index: number) => {
    handleChipClick(chip);

    if (chipsRef.current) {
      const chipElement = chipsRef.current.children[index] as HTMLElement;
      scrollToChip(chipElement);
    }
  };

  return (
    <>
      {/* Mobile Layout */}
      <div
        className="relative w-full overflow-x-auto scroll-smooth md:hidden"
        ref={containerRef}
        data-testid="mobile-chips"
      >
        <div
          ref={chipsRef}
          className={clsx(
            'flex gap-2 whitespace-nowrap',
            variant === 'inline-wrap'
              ? 'sm:justify-initial w-fit justify-center rounded-full border border-neutral-50 bg-neutral-0 p-3 lg:rounded-full lg:p-2'
              : 'gap-4',
          )}
        >
          {options.map((option, index) => (
            <Chips
              key={option}
              label={option}
              variant={'default'}
              size={'medium'}
              selectionMode={selectionMode}
              selected={selectedChips.includes(option)}
              onClick={() => handleChipClickMobile(option, index)}
            />
          ))}
        </div>
      </div>

      {/* Desktop Layout */}
      <div
        className={clsx(
          'hidden md:flex',
          variant === 'inline-wrap'
            ? 'sm:justify-initial w-fit justify-center gap-2 rounded-full border border-neutral-50 bg-neutral-0 p-3 lg:rounded-full lg:p-2'
            : 'gap-4',
        )}
        data-testid="desktop-chips"
      >
        {options.map((option) => (
          <Chips
            key={option}
            label={option}
            variant={'default'}
            size={'medium'}
            selectionMode={selectionMode}
            selected={selectedChips.includes(option)}
            onClick={() => handleChipClick(option)}
          />
        ))}
      </div>
    </>
  );
};

ChipsWrapper.displayName = 'ChipsWrapper';
export default ChipsWrapper;
